import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Card,
  CardBody,
  Checkbox,
  HStack,
  Icon,
  useColorModeValue,
  useBreakpointValue,
} from '@chakra-ui/react'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useFileProcessingStatus } from '@/hooks/useFileProcessingStatus'
import { useAppDispatch, useAppSelector } from '@/store'
import { addFilesToSelection, removeFilesFromSelection } from '@/store/slices/uiSlice'
import { TablePagination } from '@/components/Common'
import { useFileTablePreferences, FileTablePreferenceScope } from '@/hooks/useFileTablePreferences'
import {
  DEFAULT_FILE_SORT,
  FileSort,
  FileSortColumn,
  PAGE_SIZE_OPTIONS,
  sortFiles,
} from '@/utils/fileSorting'
import FileTableRow from './FileTableRow'
import { FileItem } from '@/types'

interface FileTableProps {
  files: FileItem[]
  /** localStorage bucket for sort + page size. Omit to disable persistence. */
  preferenceScope?: FileTablePreferenceScope
  /** `null` renders `files` in the order given (e.g. recency) until a header is clicked. */
  initialSort?: FileSort | null
  enablePagination?: boolean
  /** Change this to snap back to page 1 — e.g. the current folder id. */
  resetKey?: string | number
}

export default function FileTable({
  files,
  preferenceScope,
  initialSort = DEFAULT_FILE_SORT,
  enablePagination = true,
  resetKey,
}: FileTableProps) {
  const { t, currentLanguage } = useAppTranslation('common')
  const dispatch = useAppDispatch()
  const selectedFiles = useAppSelector((state) => state.ui.selectedFiles)

  const { sort, setSort, pageSize, setPageSize } = useFileTablePreferences(
    preferenceScope,
    initialSort
  )

  // Built once per language rather than per comparison — `localeCompare` would
  // construct a collator on every call.
  const collator = useMemo(
    () => new Intl.Collator(currentLanguage, { sensitivity: 'base', numeric: true }),
    [currentLanguage]
  )

  const sortedFiles = useMemo(() => sortFiles(files, sort, collator), [files, sort, collator])

  const [page, setPage] = useState(1)

  const totalPages = enablePagination ? Math.max(1, Math.ceil(sortedFiles.length / pageSize)) : 1

  // Deleting the last row of the last page, or raising the page size, can leave
  // `page` past the end.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  // Changing folder, sort or page size all mean starting over at the top.
  useEffect(() => {
    setPage(1)
  }, [resetKey, sort, pageSize])

  const pageFiles = useMemo(() => {
    if (!enablePagination) return sortedFiles
    const start = (page - 1) * pageSize
    return sortedFiles.slice(start, start + pageSize)
  }, [sortedFiles, enablePagination, page, pageSize])

  // Keeps the processing badges current, polling only the rows actually on screen and
  // only while any of them is unfinished.
  const { isStalled: processingStalled } = useFileProcessingStatus(pageFiles)

  // Also handed to each row so shift-click ranges follow the rendered order.
  const pageFileIds = useMemo(() => pageFiles.map((file) => file.id), [pageFiles])

  const selectedIdSet = useMemo(() => new Set(selectedFiles), [selectedFiles])

  // Derived from the rows this table actually renders, so the header checkbox
  // is correct wherever it is mounted — and, with pagination, speaks only for
  // the page in front of you.
  const allFilesSelected =
    pageFiles.length > 0 && pageFiles.every((file) => selectedIdSet.has(file.id))
  const someFilesSelected =
    !allFilesSelected && pageFiles.some((file) => selectedIdSet.has(file.id))

  const handleToggleAll = () => {
    if (allFilesSelected) {
      // Not `clearFileSelection` — that would drop selections made on other pages.
      dispatch(removeFilesFromSelection(pageFileIds))
    } else {
      dispatch(addFilesToSelection(pageFileIds))
    }
  }

  const handleSort = (column: FileSortColumn) => {
    if (sort?.column !== column) {
      setSort({ column, direction: 'asc' })
      return
    }

    if (sort.direction === 'asc') {
      setSort({ column, direction: 'desc' })
      return
    }

    // Where a meaningful natural order exists (recency), a third click restores it.
    setSort(initialSort === null ? null : { column, direction: 'asc' })
  }

  const tableBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const headerBg = useColorModeValue('gray.50', 'gray.750')
  const headerColor = useColorModeValue('gray.500', 'gray.400')
  const headerBorder = useColorModeValue('gray.100', 'gray.700')
  const headerHoverColor = useColorModeValue('gray.700', 'gray.200')

  // Size, type and modified are dropped on mobile, leaving name alone. `fallback`
  // avoids the flash of hidden columns on first render.
  const showDetailColumns = useBreakpointValue({ base: false, md: true }, { fallback: 'md' })
  const showSizeColumn = showDetailColumns
  const showTypeColumn = showDetailColumns
  const showModifiedColumn = showDetailColumns

  const showPagination = enablePagination && sortedFiles.length > PAGE_SIZE_OPTIONS[0]

  const sortableHeader = (
    column: FileSortColumn,
    label: string,
    { width, align = 'right' }: { width: string; align?: 'left' | 'right' }
  ) => {
    const isActive = sort?.column === column
    const ariaSort = isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'

    // Direction is already announced via aria-sort; the label describes the
    // action the click will take.
    const willClearSort = isActive && sort.direction === 'desc' && initialSort === null
    const ariaLabel = willClearSort ? t('table.clearSort') : t('table.sortBy', { column: label })

    return (
      <Th
        border="none"
        color={headerColor}
        fontWeight="700"
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="wider"
        py={2.5}
        px={3}
        w={width}
        minW={align === 'right' ? width : undefined}
        textAlign={align}
        aria-sort={ariaSort}
      >
        {/* A real button — a Th with onClick is not keyboard reachable. The UA
            button styles drop weight, letter-spacing and text-transform, so the
            Th's typography has to be restated here to match the other headers. */}
        <HStack
          as="button"
          type="button"
          spacing={1}
          w="100%"
          justify={align === 'right' ? 'flex-end' : 'flex-start'}
          fontSize="inherit"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="wider"
          onClick={() => handleSort(column)}
          aria-label={ariaLabel}
          color={isActive ? headerHoverColor : 'inherit'}
          _hover={{ color: headerHoverColor }}
          sx={{ '&:hover .sort-indicator': { opacity: 1 } }}
        >
          <Box as="span">{label}</Box>
          <Icon
            className="sort-indicator"
            as={isActive && sort.direction === 'desc' ? FiChevronDown : FiChevronUp}
            boxSize={3}
            opacity={isActive ? 1 : 0}
            transition="opacity 0.15s"
            aria-hidden
          />
        </HStack>
      </Th>
    )
  }

  return (
    <Card
      bg={tableBg}
      borderRadius="xl"
      boxShadow="none"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
    >
      <CardBody p={0}>
        <Box overflow="auto">
          <Table variant="unstyled">
            <Thead>
              <Tr bg={headerBg} borderBottom="1px solid" borderColor={headerBorder}>
                <Th border="none" py={2.5} pl={4} pr={0} w="36px" minW="36px">
                  <Checkbox
                    isChecked={allFilesSelected}
                    isIndeterminate={someFilesSelected}
                    onChange={handleToggleAll}
                    aria-label={
                      allFilesSelected ? t('buttons.deselectAll') : t('buttons.selectAll')
                    }
                  />
                </Th>
                {sortableHeader('name', t('labels.name'), { width: '100%', align: 'left' })}
                {/* Favourite toggle — deliberately unlabelled */}
                <Th border="none" py={2.5} px={2} w="48px" minW="48px" />
                {showSizeColumn &&
                  sortableHeader('file_size', t('labels.size'), { width: '104px' })}
                {showTypeColumn && sortableHeader('type', t('labels.type'), { width: '88px' })}
                {showModifiedColumn &&
                  sortableHeader('updated_at', t('labels.modified'), { width: '170px' })}
                {/* Row affordance chevron */}
                <Th border="none" py={2.5} px={3} w="36px" minW="36px" />
              </Tr>
            </Thead>
            <Tbody>
              {/* Uploads in flight are shown in the floating upload panel, not here. Once
                  the bytes have landed the row appears, and its badge tracks the OCR and
                  summary work that continues afterwards. */}
              {pageFiles.map((file, index) => (
                <FileTableRow
                  key={file.id}
                  file={file}
                  index={index}
                  showSizeColumn={showSizeColumn}
                  showTypeColumn={showTypeColumn}
                  showModifiedColumn={showModifiedColumn}
                  orderedFileIds={pageFileIds}
                  processingStalled={processingStalled}
                />
              ))}
            </Tbody>
          </Table>
        </Box>

        {showPagination && (
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={sortedFiles.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </CardBody>
    </Card>
  )
}
